// BlastCampaignModal.jsx — Launch a WhatsApp retention blast against a segment.
// One-click campaign creation + per-recipient send tracking + visual progress.
import React, { useState, useEffect, useCallback } from "react";
import { api, formatErr } from "./lib/api";
import { showToast } from "./lib/theme";
import { PhoneListUpload } from "./components/PhoneListUpload";
import { DemographicFilters } from "./components/DemographicFilters";
import { zVar } from "./lib/zIndex";
import { useCampaignSendsLock, CAMPAIGN_SENDS_LOCKED_MSG } from "./hooks/useCampaignSendsLock";

const TEMPLATES = [
  { key: "missed",   label: "💔 رسالة استرجاع",  body: "مرحباً {name}! وحشتنا في ثواني ☕ آخر زيارة لك كانت قبل فترة — عندنا قهوة ساخنة وعرض حلو ينتظرك. متى تجي؟" },
  { key: "thanks",   label: "🙏 رسالة شكر",     body: "أهلاً {name} 🌹 شكراً إنك جزء من عائلة ثواني. زيارتك تسعدنا — قهوتك القادمة على حسابنا!" },
  { key: "offer",    label: "🎁 عرض جديد",       body: "مرحباً {name}! عندنا عرض جديد في ثواني تحبه — تعال جربه وشاركنا رأيك ☕" },
  { key: "59renew",  label: "🔁 تجديد 59",        body: "أهلاً {name}! اشتراك الـ 59 كوب على وشك ينتهي. خلّه يستمر معك — جدّده اليوم بعرض خاص 🎟" },
  { key: "welcome",  label: "👋 ترحيب",           body: "أهلاً {name}! يسعدنا انضمامك لعائلة ثواني ☕ قهوتك القادمة بانتظارك — نشرّفنا بزيارتك مرة ثانية." },
  { key: "custom",   label: "✏ رسالة مخصصة",      body: "" },
];

const SEG_LABEL = {
  // Saudi local
  vip: "💎 كبار المنفقين", loyal: "✨ أوفياء", highavg: "📈 عالي الإنفاق",
  regular: "👤 عاديون", atrisk: "⚠ في خطر", lost: "💔 فقدناهم",
  new: "🆕 جدد", all: "👥 الكل",
  // RFM global
  champions: "🏆 الأبطال", loyal_customers: "💚 أوفياء (Loyal)",
  potential_loyalists: "🌱 أوفياء محتملون", new_customers: "🆕 عملاء جدد",
  promising: "🔮 واعدون", needs_attention: "👀 يحتاجون انتباه",
  about_to_sleep: "😴 على وشك النوم", at_risk: "⚠ في خطر",
  cant_lose: "🚨 لا تخسرهم", hibernating: "🌙 خاملون",
};

export default function BlastCampaignModal({ segment, segmentCount, customerNames = [], initialMessage = "", onClose }) {
  // جولة تصحيح 2026-10-14 (المرحلة 1) / البند 12 — مفتاح إيقاف الإرسال الجماعي
  const { locked: sendsLocked, stateLabel: switchLabel } = useCampaignSendsLock();
  const [step, setStep] = useState("compose"); // compose | sending | done
  const [templateKey, setTemplateKey] = useState(segment === "atrisk" || segment === "lost" ? "missed" : segment === "is59x" ? "59renew" : segment === "new" ? "welcome" : segment === "vip" ? "thanks" : "missed");
  const [message, setMessage] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [goal, setGoal] = useState("");
  const [manualPhones, setManualPhones] = useState([]);
  // v-bonat 2026-06 — فلاتر ديموغرافية (بونات) عبر المكوّن المشترك.
  const [demo, setDemo] = useState({ gender: "", age_min: null, age_max: null, include_unknown: false });
  const patchDemo = (p) => setDemo((d) => ({ ...d, ...p }));
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [idx, setIdx] = useState(0);
  // v120 — discount code pool + confirmation + billing
  const [attachCodes, setAttachCodes] = useState(false);
  const [codeTier, setCodeTier] = useState("");
  const [pool, setPool] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    api.get("/discount-codes/stats").then((r) => setPool(r.data)).catch(() => {});
  }, []);

  // AI recommender pre-fills the suggested WhatsApp text
  useEffect(() => {
    if (initialMessage) { setTemplateKey("custom"); setMessage(initialMessage); }
  }, [initialMessage]);

  // Initialize message from template
  useEffect(() => {
    const t = TEMPLATES.find((x) => x.key === templateKey);
    if (t && t.key !== "custom") setMessage(t.body);
  }, [templateKey]);

  const previewName = customerNames[0] || "محمد";
  const previewMessage = (message || "").replace("{name}", previewName);

  // v121 — one shared code per CAMPAIGN (measured later by this code)
  const manualCount = manualPhones.length;
  const neededCount = manualCount > 0 ? manualCount : segmentCount;
  const tierMatch = (b) => `${b.kind === "amount" ? "a" : "p"}:${b.value}` === codeTier;
  const availableCodes = attachCodes && codeTier
    ? (pool?.by_tier?.find(tierMatch)?.available || 0)
    : (pool?.available || 0);
  const insufficientCodes = attachCodes && availableCodes < 1;

  const launch = useCallback(async () => {
    setErr(""); setBusy(true); setConfirmOpen(false);
    try {
      const manual_phones = manualPhones;
      const { data } = await api.post("/campaigns/blast", {
        segment,
        template_key: templateKey,
        custom_message: message,
        campaign_name: campaignName,
        manual_phones,
        demographics: demo,
        attach_discount_codes: attachCodes,
        discount_pct: attachCodes && codeTier.startsWith("p:") ? Number(codeTier.slice(2)) : null,
        discount_amount: attachCodes && codeTier.startsWith("a:") ? Number(codeTier.slice(2)) : null,
      });
      setCampaign(data.campaign);
      setRecipients(data.recipients || []);
      setBilling(data.billing || null);
      setStep("sending");
      // v200.35 (أ٤) — «الجمهور N · مستبعَد M» يُعرَض في كل شاشة إرسال.
      const _excl = Number(data.excluded_invalid_count || 0);
      const _n = Number(data.audience_count || data.total || 0);
      showToast(
        `🚀 الجمهور ${_n.toLocaleString("ar")} · مستبعَد ${_excl.toLocaleString("ar")} رقماً غير صالح`,
        "success"
      );
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }, [segment, templateKey, message, campaignName, manualPhones, demo, attachCodes, codeTier]);

  // v119.239 — Layla suggests the blast text from the campaign goal
  const aiGenerate = useCallback(async () => {
    setErr(""); setAiBusy(true);
    try {
      const kindMap = { missed: "occasional_winback", welcome: "engaged_offer", thanks: "loyalist_thanks", "59renew": "value_promo" };
      const { data } = await api.post("/ai/generate-message", {
        domain: "customer_lab",
        kind: kindMap[templateKey] || "occasional_winback",
        target: "all",
        context: { "هدف الحملة": goal || "استرداد وتحفيز العملاء", "الشريحة": segment, "ملاحظة": "استخدم {name} مكان اسم العميل" },
      });
      if (data.body) setMessage(data.body);
      showToast("✨ اقترحت ليلى نص الحملة — راجعه وعدّله", "success");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "تعذّر التوليد");
    } finally { setAiBusy(false); }
  }, [goal, templateKey, segment]);

  const markStatus = useCallback(async (phone, status) => {
    try {
      await api.post(`/campaigns/${campaign.id}/blast/mark`, { phone, status });
      setRecipients((cur) => cur.map((r) => r.phone === phone ? { ...r, sent_status: status, sent_at: new Date().toISOString() } : r));
    } catch (e) { /* non-blocking */ }
  }, [campaign]);

  const sendNow = useCallback((r) => {
    window.open(r.wa_url, "_blank", "noopener");
    markStatus(r.phone, "sent");
    setIdx((cur) => cur + 1);
  }, [markStatus]);

  const skip = useCallback((r) => {
    markStatus(r.phone, "skipped");
    setIdx((cur) => cur + 1);
  }, [markStatus]);

  const counts = {
    sent: recipients.filter((r) => r.sent_status === "sent").length,
    skipped: recipients.filter((r) => r.sent_status === "skipped").length,
    pending: recipients.filter((r) => r.sent_status === "pending").length,
  };
  const progressPct = recipients.length > 0 ? (counts.sent + counts.skipped) / recipients.length * 100 : 0;

  const exportCSV = () => {
    const rows = [["الاسم", "الجوال", "الرسالة", "الحالة", "وقت الإرسال", "رابط واتساب"]];
    for (const r of recipients) rows.push([r.name, r.phone, r.message, r.sent_status, r.sent_at || "", r.wa_url]);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `blast_${segment}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="blast-modal-backdrop" onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: zVar('extra_1000'), padding: 20,
    }}>
      <div data-testid="blast-modal" onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, maxWidth: 896, width: "100%",
        maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)", position: "relative",
      }}>
        {/* ── Layer 1: FIXED HEADER ───────────────────────────────────── */}
        <div style={{
          padding: "16px 22px", flexShrink: 0,
          background: "linear-gradient(135deg, #6B4E3D 0%, #8B6F58 100%)",
          color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
              🚀 حملة استرداد — {SEG_LABEL[segment] || segment}
            </h3>
            <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 2 }}>
              {step === "compose" && `${segmentCount} عميل في هذه الشريحة`}
              {step === "sending" && `${counts.sent} مُرسلة · ${counts.skipped} تخطيت · ${counts.pending} متبقي`}
            </div>
          </div>
          <button onClick={onClose} data-testid="blast-close-btn" style={{
            background: "rgba(255,255,255,0.2)", color: "#fff", border: "none",
            borderRadius: "50%", width: 32, height: 32, cursor: "pointer", fontSize: 16,
          }}>✕</button>
        </div>

        {/* ── Layer 2: SCROLLABLE BODY (internal scrollbar only) ──────── */}
        <div data-testid="blast-modal-body" style={{ padding: 22, overflowY: "auto", flex: 1, minHeight: 0 }}>
          {step === "compose" && (
            <ComposeStep
              templateKey={templateKey} setTemplateKey={setTemplateKey}
              message={message} setMessage={setMessage}
              campaignName={campaignName} setCampaignName={setCampaignName}
              previewMessage={previewMessage} previewName={previewName}
              segmentCount={segmentCount}
              goal={goal} setGoal={setGoal} aiBusy={aiBusy} aiGenerate={aiGenerate}
              manualPhones={manualPhones} setManualPhones={setManualPhones}
              demo={demo} patchDemo={patchDemo}
              attachCodes={attachCodes} setAttachCodes={setAttachCodes}
              codeTier={codeTier} setCodeTier={setCodeTier}
              pool={pool} neededCount={neededCount}
              availableCodes={availableCodes} insufficientCodes={insufficientCodes}
            />
          )}
          {step === "sending" && (
            <SendingStep
              recipients={recipients} idx={idx} setIdx={setIdx}
              sendNow={sendNow} skip={skip} progressPct={progressPct} counts={counts}
              onExport={exportCSV} onDone={() => setStep("done")} billing={billing}
            />
          )}
          {step === "done" && (
            <div data-testid="blast-done" style={{ textAlign: "center", padding: 30 }}>
              <div style={{ fontSize: 56 }}>🎉</div>
              <h3 style={{ color: "var(--brown-deep)" }}>تمت الحملة!</h3>
              <p style={{ fontSize: 13, color: "var(--muted)" }}>
                أُرسلت <b style={{ color: "var(--success)" }}>{counts.sent}</b> رسالة ·
                تم تخطي <b>{counts.skipped}</b>.<br />
                ستُتابع نتائج الاسترداد تلقائياً في صفحة الحملات بعد عودة العملاء.
              </p>
              <button onClick={onClose} className="btn btn-primary" data-testid="blast-close-final" style={{ marginTop: 16 }}>
                إغلاق
              </button>
            </div>
          )}
          {err && <div className="al alr" style={{ marginTop: 12 }} data-testid="blast-error">{err}</div>}
        </div>

        {/* ── Layer 3: FIXED FOOTER (save/cancel always visible) ──────── */}
        {step === "compose" && (
          <div className="modal-actions-sticky" style={{
            padding: "14px 22px", borderTop: "1px solid var(--border)", flexShrink: 0,
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "var(--beige)",
          }}>
            <div style={{ fontSize: 11.5, color: "var(--muted)" }}>
              {sendsLocked ? <b style={{ color: "#b91c1c" }} data-testid="blast-locked-hint">{CAMPAIGN_SENDS_LOCKED_MSG}</b> : "💡 سيتم إنشاء حملة في صفحة الحملات تلقائياً"}
              <span style={{ marginInlineStart: 8 }} data-testid="blast-global-switch-state">مفتاح الإرسال العام: {switchLabel}</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onClose} className="btn btn-soft" data-testid="blast-cancel-btn">إلغاء</button>
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={busy || !message.trim() || insufficientCodes || sendsLocked}
                data-testid="blast-launch-btn"
                className="btn btn-primary"
                style={{ minWidth: 160 }}
              >
                {busy ? "⏳ جارٍ الإطلاق..." : `🚀 إطلاق على ${neededCount} عميل`}
              </button>
            </div>
          </div>
        )}

        {/* ── v120: Confirmation overlay — no accidental mass sends ────── */}
        {confirmOpen && (
          <div data-testid="blast-confirm-overlay" style={{
            position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: zVar('extra_10'), padding: 20, borderRadius: 16,
          }}>
            <div data-testid="blast-confirm-modal" style={{
              background: "#fff", borderRadius: 14, padding: 22, maxWidth: 440, width: "100%",
              textAlign: "center", boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
            }}>
              <div style={{ fontSize: 40 }}>⚠️</div>
              <h3 style={{ margin: "8px 0 6px", color: "var(--brown-deep)", fontSize: 17, fontWeight: 800 }}>
                تأكيد إطلاق الحملة
              </h3>
              <div style={{ fontSize: 13, color: "var(--brown-deep)", lineHeight: 2 }}>
                سترسل رسائل واتساب إلى <b>{neededCount}</b> عميل.
                {attachCodes && (
                  <div style={{ fontSize: 12, color: "#7C3AED", fontWeight: 700 }}>
                    🎟 سيُرفق كود خصم موحّد للحملة{codeTier ? ` (${codeTier.startsWith("a:") ? `قسيمة ${codeTier.slice(2)} ر.س` : `${codeTier.slice(2)}%`})` : ""} — يُعلَّم «مستخدم» ويُقاس به أثرها.
                  </div>
                )}
                <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 4 }}>
                  💡 الرسائل خارج نافذة 24 ساعة تُحتسب كقالب تسويقي مدفوع لدى Meta — سيظهر ملخص الفوترة بعد الإطلاق.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "center" }}>
                <button onClick={() => setConfirmOpen(false)} className="btn btn-soft" data-testid="blast-confirm-cancel">
                  تراجع
                </button>
                <button onClick={launch} disabled={busy || sendsLocked} className="btn btn-primary" data-testid="blast-confirm-launch"
                  style={{ minWidth: 150 }}>
                  {busy ? "⏳..." : "✅ نعم، أطلق الحملة"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Compose Step ──────────────────────────────────────────────────────
function ComposeStep({ templateKey, setTemplateKey, message, setMessage, campaignName, setCampaignName, previewMessage, previewName, segmentCount, goal, setGoal, aiBusy, aiGenerate, manualPhones, setManualPhones, demo, patchDemo, attachCodes, setAttachCodes, codeTier, setCodeTier, pool, neededCount, availableCodes, insufficientCodes }) {
  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)" }}>1️⃣ اسم الحملة (اختياري)</label>
        <input
          type="text"
          value={campaignName}
          onChange={(e) => setCampaignName(e.target.value)}
          placeholder="حملة استرداد عملاء فقدوا — يونيو 2025"
          data-testid="blast-campaign-name"
          style={{ width: "100%", padding: "9px 12px", marginTop: 6, border: "1px solid var(--border)", borderRadius: 8, fontSize: 13 }}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)" }}>2️⃣ اختر القالب</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 6, marginTop: 6 }}>
          {TEMPLATES.map((t) => (
            <button
              key={t.key}
              type="button"
              data-testid={`blast-template-${t.key}`}
              onClick={() => setTemplateKey(t.key)}
              style={{
                padding: "8px 10px",
                background: templateKey === t.key ? "var(--brown)" : "var(--beige)",
                color: templateKey === t.key ? "#fff" : "var(--brown-deep)",
                border: `1.5px solid ${templateKey === t.key ? "var(--brown)" : "transparent"}`,
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 160ms ease",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)" }}>
          3️⃣ نص الرسالة <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: 11 }}>(استخدم {"{name}"} لتشخيصها)</span>
        </label>
        {/* v119.239 — campaign goal + Layla AI generation */}
        <div style={{ display: "flex", gap: 8, marginTop: 6, marginBottom: 8 }}>
          <input type="text" value={goal} onChange={(e) => setGoal(e.target.value)}
            placeholder="🎯 هدف الحملة — مثال: استعادة المنقطعين بعرض قهوة مجانية"
            data-testid="blast-goal"
            style={{ flex: 1, padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12.5 }} />
          <button type="button" onClick={aiGenerate} disabled={aiBusy}
            data-testid="blast-ai-generate"
            style={{ padding: "9px 16px", borderRadius: 10, background: "#8b5a2b", color: "#fff", border: "none", fontWeight: 800, cursor: "pointer", fontSize: 12, opacity: aiBusy ? 0.6 : 1, whiteSpace: "nowrap" }}>
            {aiBusy ? "⏳ ليلى تكتب…" : "✨ توليد بالذكاء"}
          </button>
        </div>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="blast-message"
          rows={4}
          style={{ width: "100%", padding: 10, marginTop: 6, border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, fontFamily: "var(--f-n)", resize: "vertical" }}
        />
      </div>

      {/* v201 — رفع ملف Excel/CSV بدل النص الحر (يتجاوز الشريحة) */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)" }}>
          4️⃣ قائمة يدوية <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: 11 }}>(اختياري — ارفعي ملف أرقام؛ يتجاوز الشريحة)</span>
        </label>
        <div style={{ marginTop: 6 }}>
          <PhoneListUpload
            testid="blast-manual-upload"
            onChange={(phones) => setManualPhones(phones)}
          />
        </div>
      </div>

      {/* v-bonat — فلاتر ديموغرافية (بونات): المكوّن المشترك الوحيد */}
      <div style={{ marginBottom: 16 }} dir="rtl">
        <DemographicFilters demo={demo} setDemo={patchDemo} />
      </div>

      {/* v120 — discount code pool attachment */}
      <div style={{ marginBottom: 16, padding: 12, borderRadius: 10, border: "1px dashed #7C3AED", background: "rgba(124,58,237,0.04)" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)" }}>
          <input type="checkbox" checked={attachCodes} onChange={(e) => setAttachCodes(e.target.checked)}
            data-testid="blast-attach-codes" />
          🎟 إرفاق كود خصم موحّد للحملة (كود واحد لجميع العملاء — يُقاس به أثر الحملة)
        </label>
        {attachCodes && (
          <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select value={codeTier} onChange={(e) => setCodeTier(e.target.value)} data-testid="blast-code-pct"
                style={{ padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12.5 }}>
                <option value="">أي نوع متاح</option>
                {(pool?.by_tier || []).filter((b) => b.value).map((b) => {
                  const v = `${b.kind === "amount" ? "a" : "p"}:${b.value}`;
                  return (
                    <option key={v} value={v}>
                      {b.kind === "amount" ? `قسيمة ${b.value} ر.س` : `خصم ${b.value}%`} ({b.available} متاح)
                    </option>
                  );
                })}
              </select>
              <span data-testid="blast-codes-availability" style={{
                fontSize: 11.5, fontWeight: 700,
                color: insufficientCodes ? "#DC2626" : "#16A34A",
              }}>
                {insufficientCodes
                  ? "🚨 لا توجد أكواد متاحة في البنك — النظام أرسل طلب تغذية لمدير التشغيل تلقائياً."
                  : `✅ متاح ${availableCodes} كود — الحملة تستهلك كوداً واحداً فقط`}
              </span>
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.7 }}>
              💡 اكتب {"{code}"} في نص الرسالة لتحديد مكان الكود — أو سيُضاف تلقائياً في نهايتها.
              جميع عملاء الحملة يستلمون <b>نفس الكود</b> ليُقاس به أثر الحملة لاحقاً.
            </div>
          </div>
        )}
      </div>

      <div style={{
        background: "linear-gradient(135deg, rgba(37,211,102,0.06) 0%, rgba(20,184,166,0.04) 100%)",
        padding: 14, borderRadius: 10, borderInlineStart: "3px solid #25D366",
      }}>
        <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>
          👁 معاينة الرسالة لـ <b style={{ color: "var(--brown-deep)" }}>{previewName}</b>:
        </div>
        <div style={{ fontSize: 13, color: "var(--brown-deep)", lineHeight: 1.8, whiteSpace: "pre-wrap" }} data-testid="blast-preview">
          {previewMessage || "(اكتب الرسالة أعلاه)"}
        </div>
      </div>

      <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 14, lineHeight: 1.8 }}>
        ⚙ <b>كيف يعمل؟</b> سنفتح واتساب ويب لكل عميل واحداً تلو الآخر مع رسالته الشخصية.
        تضغط «أرسل» → يفتح تبويب واتساب جاهز فيه نص الرسالة → ترسل بنفسك → نُعلِّمها مُرسلة → ننتقل للتالي.
      </div>
    </>
  );
}

// ─── Sending Step ──────────────────────────────────────────────────────
function SendingStep({ recipients, idx, setIdx, sendNow, skip, progressPct, counts, onExport, onDone, billing }) {
  const current = recipients[idx];
  const allDone = idx >= recipients.length;

  return (
    <div data-testid="blast-sending-step">
      {/* v120 — WhatsApp 24h-window billing transparency */}
      {billing && (
        <div data-testid="blast-billing-summary" style={{
          marginBottom: 12, padding: "10px 14px", borderRadius: 10,
          background: billing.out_window > 0 ? "rgba(217,119,6,0.08)" : "rgba(22,163,74,0.08)",
          border: `1px solid ${billing.out_window > 0 ? "rgba(217,119,6,0.3)" : "rgba(22,163,74,0.25)"}`,
          fontSize: 11.5, lineHeight: 1.8, color: "var(--brown-deep)",
        }}>
          🧾 <b>فوترة واتساب:</b> {billing.in_window} عميل ضمن نافذة 24 ساعة (رد مجاني) ·{" "}
          <b style={{ color: billing.out_window > 0 ? "#B45309" : "#15803D" }}>
            {billing.out_window} خارج النافذة
          </b>{" "}
          {billing.out_window > 0 && "— تُحتسب كرسالة قالب تسويقي مدفوعة لدى Meta"}
        </div>
      )}
      {/* Progress */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 12 }}>
          <b style={{ color: "var(--brown-deep)" }}>التقدم: {idx} / {recipients.length}</b>
          <span style={{ color: "var(--muted)" }}>{progressPct.toFixed(0)}%</span>
        </div>
        <div style={{ height: 8, background: "var(--beige)", borderRadius: 8, overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: progressPct + "%",
            background: "linear-gradient(90deg, #25D366 0%, var(--success) 100%)",
            transition: "width 300ms ease",
          }} />
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 11 }}>
          <span style={{ color: "var(--success)" }}>✅ {counts.sent} مُرسلة</span>
          <span style={{ color: "var(--warn)" }}>⏭ {counts.skipped} تخطيت</span>
          <span style={{ color: "var(--muted)" }}>⏳ {counts.pending} متبقي</span>
        </div>
      </div>

      {!allDone && current ? (
        <div data-testid="blast-current-card" style={{
          padding: 18, border: "2px solid var(--gold)", borderRadius: 12,
          background: "linear-gradient(135deg, rgba(194,169,127,0.05) 0%, transparent 100%)",
          marginBottom: 14,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%",
              background: "var(--brown)", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18, fontWeight: 700,
            }}>{(current.name || "?").charAt(0)}</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--brown-deep)" }}>{current.name}</div>
              <div style={{ fontSize: 12, color: "var(--muted)" }} dir="ltr">+966 {current.phone}</div>
            </div>
          </div>
          <div style={{
            padding: 10, background: "rgba(37,211,102,0.06)",
            borderRadius: 8, fontSize: 12.5, color: "var(--brown-deep)",
            lineHeight: 1.8, marginBottom: 12, whiteSpace: "pre-wrap",
          }}>
            {current.message}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => sendNow(current)}
              data-testid="blast-send-now"
              style={{
                flex: 1, padding: "10px 14px", background: "#25D366", color: "#fff",
                border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
              }}
            >
              💬 افتح واتساب وأرسل
            </button>
            <button
              onClick={() => skip(current)}
              data-testid="blast-skip"
              style={{
                padding: "10px 14px", background: "var(--beige)", color: "var(--brown-deep)",
                border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >
              ⏭ تخطّى
            </button>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 24 }}>
          <div style={{ fontSize: 32 }}>✅</div>
          <p style={{ color: "var(--brown-deep)", fontWeight: 700, marginTop: 8 }}>أكملت كل المستلمين!</p>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button onClick={onExport} className="btn btn-soft" data-testid="blast-export-csv" style={{ flex: 1, fontSize: 12 }}>
          📥 تصدير قائمة CSV
        </button>
        {allDone && (
          <button onClick={onDone} className="btn btn-primary" data-testid="blast-finish" style={{ flex: 1, fontSize: 12 }}>
            🎉 إنهاء
          </button>
        )}
        {!allDone && idx < recipients.length - 1 && (
          <button onClick={() => setIdx(idx + 1)} className="btn btn-soft" data-testid="blast-jump-next" style={{ fontSize: 12 }}>
            تخطّى للتالي →
          </button>
        )}
      </div>
    </div>
  );
}
